/* eslint-disable no-undef */
// @flow
import api from 'api'
import { enableDisablePdfDocument } from 'api/pdfFillerApi'
import { useForm } from 'hooks/useForm'
import * as React from 'react'
import { toast } from 'react-toastify'
import { ZentakeContext } from 'utils/ZentakeContext'

import {
  invalidateLoadPackets,
  useLoadPacket,
} from '@/queries/packets/useLoadPackets'

type Props = {
  close?: () => void,
  disabled?: ?boolean,
  isPacket?: boolean,
  resourceEnabled: boolean,
  resourceId: string | number,
  resourceType: 'form' | 'packet' | 'pdf',
}

function FormControlsDropdownMenuDisableButton({
  close,
  disabled,
  isPacket,
  resourceEnabled,
  resourceId,
  resourceType,
  setLoading,
}: Props): React.Node {
  const { setAlertConfig } = React.useContext(ZentakeContext)
  const { fetchFormPage, startLoading, stopLoading } = useForm()
  const { data: packet } = useLoadPacket(resourceId, { enabled: isPacket })

  const haveForms = packet?.forms?.length > 0

  const doEnableDisableForm = React.useCallback(async () => {
    if (isPacket && !resourceEnabled && !haveForms) {
      toast.error('No Forms Associated with This Packet')
      return
    }

    try {
      const apiCall = isPacket
        ? api.enableDisablePacket
        : resourceType === 'pdf'
          ? enableDisablePdfDocument
          : api.enableDisableForm
      startLoading()
      setLoading(true)
      await apiCall(resourceId, !resourceEnabled)
      const updateCall = isPacket ? invalidateLoadPackets : fetchFormPage
      await updateCall()
      toast.success(
        `${isPacket ? 'Packet' : 'Form'} ${
          resourceEnabled ? 'disabled' : 'enabled'
        } successfully.`
      )
    } catch (error) {
      toast.error(`Error while disabling ${isPacket ? 'packet' : 'form'}.`)
    } finally {
      stopLoading && stopLoading()
      setLoading(false)
    }
  }, [
    fetchFormPage,
    haveForms,
    isPacket,
    resourceEnabled,
    resourceId,
    resourceType,
    setLoading,
    startLoading,
    stopLoading,
  ])

  const onEnableDisableForm = React.useCallback(async () => {
    if (resourceEnabled) {
      setAlertConfig({
        title: 'Attention',
        description: isPacket
          ? 'A disabled packet cannot be sent to nor completed by clients. Do you want to proceed?'
          : 'A disabled form cannot be sent to nor completed by clients. Disabled forms will be removed from all associated packets. Do you want to proceed?',
        buttons: [
          {
            title: 'Yes',
            style: 'primary',
            onPress: async () => doEnableDisableForm(),
          },
          {
            title: 'Cancel',
            onPress: () => {
              // do nothing
            },
          },
        ],
        canDismiss: false,
      })
    } else {
      await doEnableDisableForm()
    }
  }, [resourceEnabled, setAlertConfig, isPacket, doEnableDisableForm])

  const onClick = React.useCallback(
    async (e) => {
      e.preventDefault()
      e.stopPropagation()
      await onEnableDisableForm()
      if (close) {
        close()
      }
    },
    [close, onEnableDisableForm]
  )

  return (
    <button
      className="block w-full px-4 py-2 text-center text-sm leading-5 text-gray-700 transition duration-150 ease-in-out hover:bg-gray-100 focus:outline-none"
      disabled={disabled}
      onClick={onClick}
      type="button"
    >
      {resourceEnabled ? 'Disable' : 'Enable'}
    </button>
  )
}

export default FormControlsDropdownMenuDisableButton
